import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, Building2, HandCoins, Gavel, Hammer, ScrollText, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { buildFAQ, buildBreadcrumb, HOME_FAQS } from "@/lib/seo";

const HERO_IMG = "https://images.unsplash.com/photo-1544984243-ec57ea16fe25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBob3VzZSUyMGV4dGVyaW9yJTIwc3Vuc2V0fGVufDB8fHx8MTc4MjQyMDc5MXww&ixlib=rb-4.1.0&q=85";
const TRUST_IMG = "https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBoYW5kc2hha2V8ZW58MHx8fHwxNzgyNDIwNzkyfDA&ixlib=rb-4.1.0&q=85";

const SOLUTION_CARDS = [
  { icon: HandCoins, title: "Cash offers", body: "Receive multiple cash offers from vetted local investors, often with 7-21 day closes." },
  { icon: Building2, title: "List for top dollar", body: "Get listed by a verified Realtor when maximizing price is your priority." },
  { icon: ShieldCheck, title: "Foreclosure help", body: "Connect with attorneys, lenders, and loan-modification specialists." },
  { icon: Hammer, title: "Repair estimates", body: "Compare real repair bids before you decide to sell or rehab." },
  { icon: Gavel, title: "Probate & legal", body: "Find attorneys experienced with probate, divorce, and tax delinquency." },
  { icon: ScrollText, title: "Creative financing", body: "Sub-to, seller-finance, and novation offers when standard sales fall short." },
];

export default function Home() {
  return (
    <div className="bg-[var(--adn-cream)]">
      <Seo
        title="Get More Than One Option Before You Sell Your Property"
        description="ADN connects distressed homeowners with verified investors, cash buyers, Realtors, lenders, attorneys, and contractors so you can compare real options. Free for sellers."
        path="/"
        jsonLd={[
          buildFAQ(HOME_FAQS),
          buildBreadcrumb([{ name: "Home", url: "https://adn-marketplace.com/" }]),
        ]}
      />
      <Navbar />

      {/* HERO */}
      <section className="relative grain overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40">
          <div className="max-w-3xl fade-up">
            <p className="eyebrow text-[var(--adn-gold)]">Distressed Property Marketplace™</p>
            <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight mt-4">
              Get more than one option<br />
              <span className="text-[var(--adn-gold)] italic font-light">before you sell your property.</span>
            </h1>
            <p className="mt-6 text-white/80 text-lg max-w-2xl leading-relaxed">
              ADN connects homeowners with verified investors, cash buyers, agents, lenders, attorneys, and local solution providers — so you can compare real options before making a decision.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/submit-property" data-testid="hero-submit-property" className="btn-gold px-7 py-4 rounded inline-flex items-center justify-center gap-2 text-[15px]">
                Submit My Property <ArrowRight size={18} />
              </Link>
              <Link to="/for-investors" data-testid="hero-join-investor" className="btn-outline-light px-7 py-4 rounded inline-flex items-center justify-center text-[15px]">
                Join As Investor
              </Link>
              <Link to="/for-professionals" data-testid="hero-become-provider" className="text-white/85 hover:text-white px-3 py-4 text-[15px] inline-flex items-center justify-center underline-offset-4 hover:underline">
                Become A Provider →
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-white/65 text-xs uppercase tracking-[0.18em]">
              <span className="flex items-center gap-2"><Check size={14} className="text-[var(--adn-emerald)]" /> Free for sellers</span>
              <span className="flex items-center gap-2"><Check size={14} className="text-[var(--adn-emerald)]" /> Verified providers</span>
              <span className="flex items-center gap-2"><Check size={14} className="text-[var(--adn-emerald)]" /> No obligation</span>
            </div>
          </div>
        </div>
      </section>

      {/* WHY MULTIPLE OPTIONS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <p className="eyebrow">Why ADN</p>
            <h2 className="font-display text-4xl sm:text-5xl font-medium mt-3 tracking-tight">
              One submission. <em className="text-[var(--adn-gold)] not-italic">Six possible paths.</em>
            </h2>
            <p className="text-[var(--adn-text-soft)] mt-5 text-lg leading-relaxed">
              A foreclosure isn't always the only option. Inheriting a property doesn't always mean a fire sale. ADN was built so distressed sellers can see every realistic path — and choose the one that fits.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/how-it-works" className="btn-outline-navy px-5 py-3 rounded text-sm">See how it works</Link>
            </div>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
            {SOLUTION_CARDS.map((s, i) => (
              <div key={i} className="bg-white border border-[var(--adn-line)] p-6 card-hover">
                <s.icon size={22} className="text-[var(--adn-gold)]" />
                <h3 className="mt-4 font-display font-semibold text-lg">{s.title}</h3>
                <p className="text-sm text-[var(--adn-text-soft)] mt-1.5 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS BAR */}
      <section className="bg-white border-y border-[var(--adn-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-3 gap-10">
          {[
            { n: "01", t: "Submit your property", d: "Tell us once — address, situation, photos. AI helps fill in the blanks." },
            { n: "02", t: "Get multiple options", d: "Verified investors, agents, attorneys, lenders, and contractors respond." },
            { n: "03", t: "Compare & choose", d: "Side-by-side offers. Net to seller, timeline, conditions — clear and neutral." },
          ].map((s) => (
            <div key={s.n}>
              <div className="text-[var(--adn-gold)] font-display text-5xl font-light tabular">{s.n}</div>
              <h3 className="font-display text-xl font-semibold mt-3">{s.t}</h3>
              <p className="text-[var(--adn-text-soft)] mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST / INVESTORS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="eyebrow">For investors & providers</p>
            <h2 className="font-display text-4xl sm:text-5xl font-medium mt-3 tracking-tight">
              Real motivated sellers.<br />
              <span className="text-[var(--adn-text-soft)]">Verified, organized, ready.</span>
            </h2>
            <ul className="mt-8 space-y-4 text-[15px]">
              {[
                "City and county-level filtering",
                "Situation tags: foreclosure, probate, tax, vacant",
                "AI-generated summaries on every lead",
                "Built-in messaging and scheduling",
                "Proof-of-funds upload and admin verification",
              ].map((x) => (
                <li key={x} className="flex items-start gap-3 text-[var(--adn-text)]">
                  <span className="mt-1 h-5 w-5 rounded-full bg-[var(--adn-emerald)]/10 text-[var(--adn-emerald)] flex items-center justify-center"><Check size={12} /></span>
                  <span>{x}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex gap-3">
              <Link to="/for-investors" className="btn-gold px-6 py-3 rounded text-sm">Become an Investor</Link>
              <Link to="/pricing" className="btn-outline-navy px-6 py-3 rounded text-sm">See pricing</Link>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative overflow-hidden border border-[var(--adn-line)]">
              <img src={TRUST_IMG} alt="Professionals meeting" className="w-full h-[420px] object-cover" />
              <div className="absolute bottom-4 left-4 bg-white/95 border border-[var(--adn-line)] px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--adn-text-soft)]">Verified provider</div>
                <div className="font-display font-semibold mt-0.5">Approved by ADN review team</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-[var(--adn-navy)] text-white grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 text-center">
          <Sparkles size={28} className="text-[var(--adn-gold)] mx-auto" />
          <h2 className="font-display text-4xl sm:text-5xl font-light mt-4 tracking-tight">
            Stop guessing. <span className="text-[var(--adn-gold)]">Start comparing.</span>
          </h2>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto">
            Free for sellers. No obligation. Get matched with verified options in minutes.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/submit-property" data-testid="cta-submit-property" className="btn-gold px-7 py-4 rounded text-sm">Submit My Property</Link>
            <Link to="/login" data-testid="cta-login" className="btn-outline-light px-7 py-4 rounded text-sm">Login</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
